// src/components/modals/VIPRoomReservationModal.jsx
import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { X, Calendar, Clock, Users, DollarSign } from 'lucide-react'
import ApiService from '../../services/api'
import toast from 'react-hot-toast'

const VIPRoomReservationModal = ({ isOpen, onClose, branchId }) => {
  const [loading, setLoading] = useState(false)
  const [vipRooms, setVipRooms] = useState([])
  const [reservations, setReservations] = useState([])
  const [selectedRoom, setSelectedRoom] = useState(null)
  const [editingReservation, setEditingReservation] = useState(null)
  const [originalData, setOriginalData] = useState(null)
  const [editFormData, setEditFormData] = useState({
    vipRoom: null,
    startTime: '',
    endTime: '',
    note: '',
    status: ''
  })
  const [formData, setFormData] = useState({
    startTime: '',
    endTime: '',
    note: ''
  })

  useEffect(() => {
    if (isOpen && branchId) {
      fetchVIPRooms()
      fetchReservations()
    }
  }, [isOpen, branchId])

  const fetchVIPRooms = async () => {
    try {
      const response = await ApiService.get(`/vip-rooms?branch=${branchId}`)
      
      if (response.success && response.data?.rooms) {
        setVipRooms(response.data.rooms)
      }
    } catch (error) {
      console.error('Error fetching VIP rooms:', error)
      toast.error('Failed to load VIP rooms')
    }
  }

  const fetchReservations = async () => {
    try {
      setLoading(true)
      const response = await ApiService.get('/vip-room-reservations')
      
      if (response.success && response.data?.reservations) {
        setReservations(response.data.reservations)
      }
    } catch (error) {
      console.error('Error fetching reservations:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!selectedRoom) {
      toast.error('Please select a VIP room')
      return
    }

    if (!formData.startTime || !formData.endTime) {
      toast.error('Please select start and end time')
      return
    }

    try {
      setLoading(true)

      const reservationData = {
        vipRoom: selectedRoom._id,
        startTime: new Date(formData.startTime).toISOString(),
        endTime: new Date(formData.endTime).toISOString(),
        note: formData.note || ''
      }

      const response = await ApiService.post('/vip-room-reservations', reservationData)

      if (response.success) {
        toast.success(response.message || 'Reservation created successfully')
        resetForm()
        fetchReservations()
      }
    } catch (error) {
      console.error('Error creating reservation:', error)
      toast.error(error.response?.data?.message || 'Failed to create reservation')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdate = async (reservationId) => {
    if (!editFormData.status) {
      toast.error('Please select a status')
      return
    }

    if (!editFormData.vipRoom) {
      toast.error('Please select a VIP room')
      return
    }

    if (!editFormData.startTime || !editFormData.endTime) {
      toast.error('Please select start and end time')
      return
    }

    try {
      setLoading(true)

      // Build payload with only changed fields
      const updateData = {}

      if (editFormData.vipRoom._id !== originalData.vipRoom._id) {
        updateData.vipRoom = editFormData.vipRoom._id
      }

      const editedStartTime = new Date(editFormData.startTime).toISOString()
      const originalStartTime = new Date(originalData.startTime).toISOString()
      if (editedStartTime !== originalStartTime) {
        updateData.startTime = editedStartTime
      }

      const editedEndTime = new Date(editFormData.endTime).toISOString()
      const originalEndTime = new Date(originalData.endTime).toISOString()
      if (editedEndTime !== originalEndTime) {
        updateData.endTime = editedEndTime
      }

      if ((editFormData.note || '') !== (originalData.note || '')) {
        updateData.note = editFormData.note || ''
      }

      if (editFormData.status !== originalData.status) {
        updateData.status = editFormData.status
      }

      // Check if there are any changes
      if (Object.keys(updateData).length === 0) {
        toast.info('No changes to update')
        cancelEditing()
        return
      }

      const response = await ApiService.patch(`/vip-room-reservations/${reservationId}`, updateData)

      if (response.success) {
        toast.success(response.message || 'Reservation updated successfully')
        setEditingReservation(null)
        setOriginalData(null)
        setEditFormData({
          vipRoom: null,
          startTime: '',
          endTime: '',
          note: '',
          status: ''
        })
        fetchReservations()
      }
    } catch (error) {
      console.error('Error updating reservation:', error)
      toast.error(error.response?.data?.message || 'Failed to update reservation')
    } finally {
      setLoading(false)
    }
  }

  const startEditing = (reservation) => {
    setEditingReservation(reservation._id)
    
    // Store original data for comparison
    setOriginalData({
      vipRoom: reservation.vipRoom,
      startTime: reservation.startTime,
      endTime: reservation.endTime,
      note: reservation.note || '',
      status: reservation.status
    })
    
    setEditFormData({
      vipRoom: reservation.vipRoom,
      startTime: new Date(reservation.startTime).toISOString().slice(0, 16),
      endTime: new Date(reservation.endTime).toISOString().slice(0, 16),
      note: reservation.note || '',
      status: reservation.status
    })
  }

  const cancelEditing = () => {
    setEditingReservation(null)
    setOriginalData(null)
    setEditFormData({
      vipRoom: null,
      startTime: '',
      endTime: '',
      note: '',
      status: ''
    })
  }

  const resetForm = () => {
    setSelectedRoom(null)
    setFormData({
      startTime: '',
      endTime: '',
      note: ''
    })
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const calculateDuration = () => {
    if (!formData.startTime || !formData.endTime) return 0
    const start = new Date(formData.startTime)
    const end = new Date(formData.endTime)
    const hours = (end - start) / (1000 * 60 * 60)
    return Math.max(0, hours)
  }

  const calculatePrice = () => {
    if (!selectedRoom) return 0
    const duration = calculateDuration()
    const discount = selectedRoom.discount || 0
    const basePrice = selectedRoom.pricePerHour * duration
    const discountAmount = (basePrice * discount) / 100
    return Math.round(basePrice - discountAmount)
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      case 'completed':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
      style={{ zIndex: 99999999 }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-orange-500 to-orange-600">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Users className="h-5 w-5" />
            VIP Room Reservations
          </h2>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Create Reservation */}
            <div className="space-y-6">
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Reservation</h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* VIP Room Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select VIP Room *
                    </label>
                    <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                      {vipRooms.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No active VIP rooms available
                        </p>
                      ) : (
                        vipRooms.map((room) => (
                          <button
                            key={room._id}
                            type="button"
                            onClick={() => setSelectedRoom(room)}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              selectedRoom?._id === room._id
                                ? 'border-orange-500 bg-orange-50'
                                : 'border-gray-200 bg-white hover:border-orange-300'
                            }`}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900">{room.name}</p>
                                <p className="text-sm text-gray-600 capitalize">{room.category}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-orange-600">
                                  {formatCurrency(room.pricePerHour)}/hr
                                </p>
                                {room.discount > 0 && (
                                  <p className="text-xs text-green-600">
                                    {room.discount}% off
                                  </p>
                                )}
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Start Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="inline h-4 w-4 mr-1" />
                      Start Time *
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.startTime}
                      onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>

                  {/* End Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock className="inline h-4 w-4 mr-1" />
                      End Time *
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.endTime}
                      onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>

                  {/* Price Calculation */}
                  {selectedRoom && formData.startTime && formData.endTime && (
                    <div className="bg-white rounded-lg p-4 border-2 border-orange-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Duration:</span>
                        <span className="font-medium">{calculateDuration().toFixed(1)} hours</span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Rate per hour:</span>
                        <span className="font-medium">{formatCurrency(selectedRoom.pricePerHour)}</span>
                      </div>
                      {selectedRoom.discount > 0 && (
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600">Discount:</span>
                          <span className="font-medium text-green-600">{selectedRoom.discount}%</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                        <span className="font-semibold text-gray-900">Total Price:</span>
                        <span className="text-xl font-bold text-orange-600">
                          {formatCurrency(calculatePrice())}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Note */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Note (Optional)
                    </label>
                    <textarea
                      value={formData.note}
                      onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Any special requests or notes..."
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading || !selectedRoom}
                    className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Creating...' : 'Create Reservation'}
                  </button>
                </form>
              </div>
            </div>

            {/* Right Column - Existing Reservations */}
            <div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Today's Reservations ({reservations.length})
                </h3>
                
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {loading ? (
                    <p className="text-center text-gray-500 py-8">Loading reservations...</p>
                  ) : reservations.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No reservations found</p>
                  ) : (
                    reservations.map((reservation) => (
                      <div
                        key={reservation._id}
                        className="bg-white rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow"
                      >
                        {editingReservation === reservation._id ? (
                          /* Edit Mode */
                          <div className="space-y-3">
                            <h4 className="font-semibold text-gray-900 mb-3">Edit Reservation</h4>
                            
                            {/* VIP Room Selection */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">VIP Room</label>
                              <select
                                value={editFormData.vipRoom?._id || ''}
                                onChange={(e) => {
                                  const room = vipRooms.find(r => r._id === e.target.value)
                                  setEditFormData({ ...editFormData, vipRoom: room })
                                }}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                              >
                                {vipRooms.map((room) => (
                                  <option key={room._id} value={room._id}>
                                    {room.name} - {formatCurrency(room.pricePerHour)}/hr
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Start Time */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Start Time</label>
                              <input
                                type="datetime-local"
                                value={editFormData.startTime}
                                onChange={(e) => setEditFormData({ ...editFormData, startTime: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                              />
                            </div>

                            {/* End Time */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">End Time</label>
                              <input
                                type="datetime-local"
                                value={editFormData.endTime}
                                onChange={(e) => setEditFormData({ ...editFormData, endTime: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                              />
                            </div>

                            {/* Status */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                              <select
                                value={editFormData.status}
                                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                              >
                                <option value="confirmed">Confirmed</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                              </select>
                            </div>

                            {/* Note */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Note</label>
                              <textarea
                                value={editFormData.note}
                                onChange={(e) => setEditFormData({ ...editFormData, note: e.target.value })}
                                rows={2}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                                placeholder="Optional note..."
                              />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2 pt-2">
                              <button
                                onClick={() => handleUpdate(reservation._id)}
                                disabled={loading}
                                className="flex-1 bg-orange-500 text-white py-2 px-4 rounded-lg text-sm font-semibold hover:bg-orange-600 transition-colors disabled:bg-gray-300"
                              >
                                {loading ? 'Updating...' : 'Save Changes'}
                              </button>
                              <button
                                onClick={cancelEditing}
                                disabled={loading}
                                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg text-sm font-semibold hover:bg-gray-400 transition-colors disabled:bg-gray-200"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* View Mode */
                          <>
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-semibold text-gray-900">
                                  {reservation.vipRoom?.name || 'N/A'}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {reservation.customer?.name || 'Walk-in Customer'}
                                </p>
                              </div>
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(reservation.status)}`}>
                                {reservation.status}
                              </span>
                            </div>
                            
                            <div className="space-y-1 text-sm text-gray-600">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                {new Date(reservation.startTime).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                {new Date(reservation.startTime).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                                {' - '}
                                {new Date(reservation.endTime).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                              <div className="flex items-center gap-2">
                                <DollarSign className="h-4 w-4" />
                                <span className="font-semibold text-orange-600">
                                  {formatCurrency(reservation.totalPrice)}
                                </span>
                              </div>
                            </div>

                            {reservation.note && (
                              <p className="mt-2 text-sm text-gray-600 italic border-t pt-2">
                                {reservation.note}
                              </p>
                            )}

                            {/* Action Button */}
                            <div className="mt-3">
                              <button
                                onClick={() => startEditing(reservation)}
                                className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg text-sm font-semibold hover:bg-blue-600 transition-colors"
                              >
                                Edit Reservation
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

VIPRoomReservationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  branchId: PropTypes.string
}

export default VIPRoomReservationModal
